import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import Loading from '../../components/Loading';
import { getIcon } from '../../utils/iconMapper';
import { auditoriaService } from '../../services/auditoriaService';
import { empresasService } from '../../services/empresasService';
import { sucursalesService } from '../../services/sucursalesService';
import { usuariosService } from '../../services/usuariosService';
import SnowFallComponent from 'components/SnowFallComponent';

function Auditoria() {
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);

  // Filtros
  const [empresa, setEmpresa] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroSucursal, setFiltroSucursal] = useState('');
  const [filtroModulo, setFiltroModulo] = useState('');
  const [filtroTipoAccion, setFiltroTipoAccion] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

  // Módulos disponibles (basados en los módulos del sistema)
  const modulos = [
    'Empresas',
    'Sucursales',
    'Clientes',
    'Productos',
    'Categorias',
    'Inventario',
    'Ventas',
    'Facturacion',
    'Usuarios',
    'Roles',
    'Permisos',
    'Reportes',
    'Auditoria',
  ];

  // Tipos de acción comunes
  const tiposAccion = [
    'Create',
    'Update',
    'Delete',
    'Read',
    'Login',
    'Logout',
    'Cancel',
    'Generate',
    'Export',
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!loadingInitial && empresa) {
      loadLogs();
    }
  }, [filtroUsuario, filtroSucursal, filtroModulo, filtroTipoAccion, filtroFechaDesde, filtroFechaHasta, loadingInitial, empresa]);

  const loadInitialData = async () => {
    try {
      setLoadingInitial(true);
      
      // Cargar empresa activa
      const empresasResponse = await empresasService.getAll();
      if (empresasResponse.success && empresasResponse.data) {
        const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        if (empresaActiva) {
          setEmpresa(empresaActiva);
          
          // Cargar sucursales
          const sucursalesResponse = await sucursalesService.getAll(empresaActiva.EmpresaID || empresaActiva.empresaID);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            setSucursales(sucursalesData.map(suc => ({
              SucursalID: suc.SucursalID || suc.sucursalID,
              Nombre: suc.Nombre || suc.nombre || '',
            })));
          }

          // Cargar usuarios
          const usuariosResponse = await usuariosService.getAll();
          if (usuariosResponse.success && usuariosResponse.data) {
            const usuariosData = Array.isArray(usuariosResponse.data) ? usuariosResponse.data : [];
            setUsuarios(usuariosData.map(usr => ({
              UsuarioID: usr.UsuarioID || usr.usuarioID,
              Nombre: usr.Nombre || usr.nombre || '',
              Email: usr.Email || usr.email || '',
            })));
          }
        }
      }
    } catch (err) {
      setError('Error al cargar datos iniciales');
    } finally {
      setLoadingInitial(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await auditoriaService.getAll(
        filtroUsuario ? parseInt(filtroUsuario) : null,
        empresa?.EmpresaID || empresa?.empresaID || null,
        filtroSucursal ? parseInt(filtroSucursal) : null,
        filtroModulo || null,
        filtroTipoAccion || null,
        filtroFechaDesde || null,
        filtroFechaHasta || null
      );

      if (response.success && response.data) {
        const logsData = Array.isArray(response.data) ? response.data : [];
        const normalized = logsData.map(log => ({
          LogID: log.LogID || log.logID,
          UsuarioID: log.UsuarioID || log.usuarioID,
          UsuarioNombre: log.UsuarioNombre || log.usuarioNombre || 'N/A',
          EmpresaID: log.EmpresaID || log.empresaID,
          EmpresaNombre: log.EmpresaNombre || log.empresaNombre || 'N/A',
          SucursalID: log.SucursalID || log.sucursalID,
          SucursalNombre: log.SucursalNombre || log.sucursalNombre || 'N/A',
          TipoAccion: log.TipoAccion || log.tipoAccion || '',
          Modulo: log.Modulo || log.modulo || '',
          EntidadID: log.EntidadID || log.entidadID,
          Descripcion: log.Descripcion || log.descripcion || '',
          FechaAccion: log.FechaAccion || log.fechaAccion,
          IPAddress: log.IPAddress || log.ipAddress || 'N/A',
        }));
        setLogs(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar los logs de auditoría');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = () => {
    if (!logs || logs.length === 0) {
      setError('No hay datos para exportar');
      return;
    }

    const columnas = [
      { label: 'Fecha', accessor: 'FechaAccion' },
      { label: 'Usuario', accessor: 'UsuarioNombre' },
      { label: 'Módulo', accessor: 'Modulo' },
      { label: 'Tipo Acción', accessor: 'TipoAccion' },
      { label: 'Descripción', accessor: 'Descripcion' },
      { label: 'Sucursal', accessor: 'SucursalNombre' },
      { label: 'IP', accessor: 'IPAddress' },
    ];

    // Crear encabezados
    const headers = columnas.map(col => col.label).join(',');
    
    // Crear filas
    const rows = logs.map(log => {
      return columnas.map(col => {
        let value = log[col.accessor] || '';
        if (col.accessor === 'FechaAccion' && value) {
          value = new Date(value).toLocaleString('es-MX');
        }
        // Escapar comillas y envolver en comillas si contiene comas
        const stringValue = String(value).replace(/"/g, '""');
        return stringValue.includes(',') ? `"${stringValue}"` : stringValue;
      }).join(',');
    });

    // Combinar todo
    const csvContent = [headers, ...rows].join('\n');
    
    // Crear blob y descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `logs_auditoria_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (err) {
      return dateString;
    }
  };

  const getBadgeColor = (tipoAccion) => {
    const tipo = tipoAccion?.toLowerCase() || '';
    if (tipo === 'create' || tipo === 'generate') return 'success';
    if (tipo === 'update') return 'warning';
    if (tipo === 'delete' || tipo === 'cancel') return 'danger';
    if (tipo === 'login') return 'info';
    if (tipo === 'logout') return 'secondary';
    return 'primary';
  };

  const columns = [
    {
      key: 'fechaAccion',
      label: 'Fecha',
      accessor: 'FechaAccion',
      render: (value) => formatDateTime(value),
    },
    {
      key: 'usuario',
      label: 'Usuario',
      accessor: 'UsuarioNombre',
    },
    {
      key: 'modulo',
      label: 'Módulo',
      accessor: 'Modulo',
    },
    {
      key: 'tipoAccion',
      label: 'Tipo Acción',
      accessor: 'TipoAccion',
      render: (value) => (
        <Badge color={getBadgeColor(value)}>
          {value || 'N/A'}
        </Badge>
      ),
    },
    {
      key: 'descripcion',
      label: 'Descripción',
      accessor: 'Descripcion',
      render: (value) => value || 'N/A',
    },
    {
      key: 'sucursal',
      label: 'Sucursal',
      accessor: 'SucursalNombre',
    },
    {
      key: 'ipAddress',
      label: 'IP',
      accessor: 'IPAddress',
    },
  ];

  const limpiarFiltros = () => {
    setFiltroUsuario('');
    setFiltroSucursal('');
    setFiltroModulo('');
    setFiltroTipoAccion('');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
  };

  if (loadingInitial) {
    return (
      <div className="content">
        <SnowFallComponent />
        <Loading />
      </div>
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Auditoría</CardTitle>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <Row className="mb-3">
                <Col md="2">
                  <label>Usuario</label>
                  <Input
                    type="select"
                    value={filtroUsuario}
                    onChange={(e) => setFiltroUsuario(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {usuarios.map((usr) => (
                      <option key={usr.UsuarioID} value={usr.UsuarioID}>
                        {usr.Nombre} ({usr.Email})
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="2">
                  <label>Sucursal</label>
                  <Input
                    type="select"
                    value={filtroSucursal}
                    onChange={(e) => setFiltroSucursal(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {sucursales.map((suc) => (
                      <option key={suc.SucursalID} value={suc.SucursalID}>
                        {suc.Nombre}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="2">
                  <label>Módulo</label>
                  <Input
                    type="select"
                    value={filtroModulo}
                    onChange={(e) => setFiltroModulo(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {modulos.map((mod) => (
                      <option key={mod} value={mod}>
                        {mod}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="2">
                  <label>Tipo Acción</label>
                  <Input
                    type="select"
                    value={filtroTipoAccion}
                    onChange={(e) => setFiltroTipoAccion(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {tiposAccion.map((tipo) => (
                      <option key={tipo} value={tipo}>
                        {tipo}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="2">
                  <label>Fecha Desde</label>
                  <Input
                    type="date"
                    value={filtroFechaDesde}
                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                  />
                </Col>
                <Col md="2">
                  <label>Fecha Hasta</label>
                  <Input
                    type="date"
                    value={filtroFechaHasta}
                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md="12" className="text-right">
                  <Button color="secondary" onClick={limpiarFiltros} className="mr-2">
                    <i className={getIcon("tim-icons icon-refresh-02")} /> <span className="d-none d-md-inline">Limpiar Filtros</span>
                  </Button>
                  <Button
                    color="success"
                    onClick={exportarCSV}
                    disabled={logs.length === 0}
                  >
                    <i className="fas fa-file-excel" /> <span className="d-none d-md-inline">Exportar CSV</span>
                  </Button>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  {loading ? (
                    <Loading />
                  ) : (
                    <>
                      <div className="mb-2">
                        <strong>Total de registros: {logs.length}</strong>
                      </div>
                      <DataTable columns={columns} data={logs} />
                    </>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Auditoria;

